
import React from "react";
import { motion } from "framer-motion";

function BusinessBillingFeatures() {
  const features = [
    {
      title: "GST-Compliant Invoicing",
      description: "Easily create and share GST-compliant invoices in seconds, ensuring your business stays tax-ready and legally compliant at all times.",
      color: "#4CAF50",
      direction: "left",
       
    },
    {
      title: "Real-Time Sales Tracking",
      description: "Monitor sales and transactions in real time with detailed analytics, helping you understand customer trends and improve decision-making.",
      color: "#2196F3",
      direction: "right",
       
    },
    {
      title: "Inventory Management",
      description: "Track stock levels, get low-stock alerts, and manage purchases effortlessly so you never miss a sale due to stockouts.",
      color: "#FF9800",
      direction: "top",
       
    },
    {
      title: "Automated Reports",
      description: "Generate daily, weekly, or monthly financial reports instantly to keep your accounts up-to-date and audit-ready.",
      color: "#9C27B0",
      direction: "bottom",
       
    },
    {
      title: "Multi-Device Access",
      description: "Access Maamnas Billing Software from desktop, tablet, or mobile - anytime, anywhere-so you can run your business on the go.",
      color: "#00BCD4",
      direction: "left",
       
    },
    {
      title: "Customer Management",
      description: "Maintain customer database, track purchase history, and manage customer communications for better relationship management.",
      color: "#E91E63",
      direction: "right",
       
    },
  ];

  // Animation variants based on direction
  const getCardVariants = (direction) => {
    const distance = 100;
    
    switch(direction) {
      case "left":
        return {
          hidden: { opacity: 0, x: -distance },
          visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.8, ease: "easeOut" }
          }
        };
      case "right":
        return {
          hidden: { opacity: 0, x: distance },
          visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.8, ease: "easeOut" }
          }
        };
      case "top":
        return {
          hidden: { opacity: 0, y: -distance },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" }
          }
        };
      case "bottom":
        return {
          hidden: { opacity: 0, y: distance },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" }
          }
        };
      default:
        return {
          hidden: { opacity: 0, y: 50 },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" }
          }
        };
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        duration: 0.5,
      },
    },
  };

  const styles = {
    container: {
      padding: "60px 20px",
      textAlign: "center",
      background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      minHeight: "100vh",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    heading: {
      fontSize: "32px",
      marginBottom: "20px",
      fontWeight: "600",
      color: "black",
      letterSpacing: "0px",
    },
    subheading: {
      fontSize: "clamp(16px, 3vw, 20px)",
      marginBottom: "50px",
      color: "black",
      maxWidth: "800px",
      margin: "0 auto 50px",
      lineHeight: "1.6",
      fontWeight: "500",
      textAlign:"justify",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 350px), 1fr))",
      gap: "30px",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    card: {
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
      padding: "25px 20px",
      textAlign: "left",
      transition: "all 0.3s ease",
      borderLeft: "6px solid transparent",
      minHeight: "220px", // Reduced from 280px
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    titleContainer: {
      display: "flex",
      alignItems: "center",
      marginBottom: "12px", // Slightly reduced
    },
    icon: {
      fontSize: "28px",
      marginRight: "15px",
    },
    title: {
      fontSize: "clamp(18px, 2.5vw, 22px)",
      fontWeight: "600",
      color: "#2c3e50",
      lineHeight: "1.4",
      margin: 0,
    },
    desc: {
      fontSize: "clamp(14px, 2vw, 16px)",
      lineHeight: "1.6",
      color: "#5a6773",
    },
  };

  return (
    <div style={styles.container}>
      <motion.h2
        style={styles.heading}
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        Six Reasons Why Business Choose Billing Software
      </motion.h2>
      
      <motion.p 
        style={styles.subheading}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        Streamline your business operations with our comprehensive billing solution
      </motion.p>

      <motion.div
        style={styles.grid}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {features.map((feature, index) => (
          <motion.div
            key={index}
            variants={getCardVariants(feature.direction)}
            style={{
              ...styles.card,
              borderLeft: `6px solid ${feature.color}`,
            }}
            whileHover={{
              scale: 1.03,
              boxShadow: "0 15px 35px rgba(0,0,0,0.12)",
              transition: { duration: 0.2 },
            }}
          >
            <div>
              <div style={styles.titleContainer}>
                <span style={styles.icon}>{feature.icon}</span>
                <h3 style={styles.title}>{feature.title}</h3>
              </div>
              <p style={styles.desc}>{feature.description}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
      <div style={{ textAlign: "center" }}>
  <a
    href="https://maamaas.com/"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      textDecoration: "none",
    }}
  >
    <button
      style={{
        backgroundColor: "#0d7a93",
        color: "#ffffff",
        border: "none",
        padding: "12px 28px",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: "600",
        cursor: "pointer",
        marginTop: "40px",
      }}
    >
      Visit Our Website
    </button>
  </a>
</div>
    </div>
    
  );
}

export default BusinessBillingFeatures;